/**
 * 직업 스킬 카탈로그. 수치는 `skills.json`, 레벨별 계수는 resolveSkill 이 계산한다.
 */

import seed from './skills.json';
import { emitDataReloadSoon, replaceRecord, requireRecord } from './catalog';
import type { SkillDef } from './balance';

export interface SkillBuffScale {
  id: string;
  durationBase: number;
  durationPerLevel: number;
  defenseMulBase?: number;
  defenseMulPerLevel?: number;
  damageMulBase?: number;
  damageMulPerLevel?: number;
  moveMulBase?: number;
  moveMulPerLevel?: number;
}

export interface SkillCatalogEntry {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  icon: string;
  jobId: string;
  kind: SkillDef['kind'];
  maxLevel: number;
  mpBase: number;
  mpPerLevel: number;
  damageBase: number;
  damagePerLevel: number;
  hits: number;
  maxTargets: number;
  hitbox: SkillDef['hitbox'];
  cooldownMs: number;
  castMs: number;
  hitFrameRatio: number;
  knockback: number;
  lungeVelocity: number;
  anim: SkillDef['anim'];
  effect: SkillDef['effect'];
  projectile?: NonNullable<SkillDef['projectile']>;
  buff?: SkillBuffScale;
}

export const SKILL_CATALOG: Record<string, SkillCatalogEntry> = structuredClone(
  seed.SKILLS,
) as Record<string, SkillCatalogEntry>;

export function applySkills(raw: unknown): void {
  const data = requireRecord(raw, 'skills.json');
  replaceRecord(
    SKILL_CATALOG,
    structuredClone(requireRecord(data.SKILLS, 'SKILLS')) as Record<string, SkillCatalogEntry>,
  );
}

export function getSkillEntry(id: string): SkillCatalogEntry | undefined {
  return SKILL_CATALOG[id];
}

function scale(base: number | undefined, per: number | undefined, level: number): number | undefined {
  if (base === undefined) return undefined;
  return base + (per ?? 0) * Math.max(0, level - 1);
}

/** 스킬 레벨 0 이면 undefined (아직 배우지 않음) */
export function resolveSkill(id: string, level: number): SkillDef | undefined {
  const entry = SKILL_CATALOG[id];
  if (!entry || level < 1) return undefined;
  const lv = Math.min(entry.maxLevel, Math.max(1, Math.floor(level)));
  const buff = entry.buff
    ? {
        id: entry.buff.id,
        durationMs: Math.round(entry.buff.durationBase + entry.buff.durationPerLevel * (lv - 1)),
        defenseMul: scale(entry.buff.defenseMulBase, entry.buff.defenseMulPerLevel, lv),
        damageMul: scale(entry.buff.damageMulBase, entry.buff.damageMulPerLevel, lv),
        moveMul: scale(entry.buff.moveMulBase, entry.buff.moveMulPerLevel, lv),
      }
    : undefined;
  return {
    id: entry.id,
    name: entry.name,
    nameEn: entry.nameEn,
    description: entry.description,
    icon: entry.icon,
    mpCost: entry.mpBase + entry.mpPerLevel * (lv - 1),
    damageMultiplier: entry.damageBase + entry.damagePerLevel * (lv - 1),
    hits: entry.hits,
    maxTargets: entry.maxTargets,
    hitbox: { ...entry.hitbox },
    cooldownMs: entry.cooldownMs,
    castMs: entry.castMs,
    hitFrameRatio: entry.hitFrameRatio,
    knockback: entry.knockback,
    lungeVelocity: entry.lungeVelocity,
    anim: entry.anim,
    effect: entry.effect,
    kind: entry.kind,
    projectile: entry.projectile ? { ...entry.projectile } : undefined,
    buff,
  };
}

if (import.meta.hot) {
  import.meta.hot.accept('./skills.json', (mod) => {
    if (!mod?.default) return;
    applySkills(mod.default);
    emitDataReloadSoon();
  });
}
